import { actAs } from './act-as';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: { message?: string | string[]; code?: string; [k: string]: unknown },
  ) {
    super(ApiError.describe(status, body));
  }

  get code(): string | undefined {
    return this.body.code;
  }

  static describe(status: number, body: ApiError['body']): string {
    const m = body.message;
    if (Array.isArray(m)) return m.join('. ');
    if (typeof m === 'string' && m) return m;
    return status >= 500 ? 'Something went wrong on our side. Please try again.' : `Request failed (${status})`;
  }
}

type Query = Record<string, string | number | boolean | null | undefined>;

function withQuery(path: string, query?: Query) {
  if (!query) return path;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined && v !== null && v !== '') params.set(k, String(v));
  }
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}

async function request<T>(method: string, path: string, opts: { query?: Query; body?: unknown; form?: FormData } = {}): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (method !== 'GET') headers['X-Fraud-Safe-CSRF'] = '1';
  const client = actAs.get();
  if (client) headers['X-Act-As-Client'] = client;
  let body: BodyInit | undefined;
  if (opts.form) body = opts.form;
  else if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(opts.body);
  }

  const res = await fetch(withQuery(path, opts.query), { method, headers, body, credentials: 'same-origin' });
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const data = text ? safeJson(text) : undefined;
  if (!res.ok) throw new ApiError(res.status, (data as ApiError['body']) ?? {});
  return data as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return { message: text };
  }
}

export const api = {
  get: <T>(path: string, query?: Query) => request<T>('GET', path, { query }),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, { body }),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, { body }),
  upload: <T>(path: string, form: FormData, query?: Query) => request<T>('POST', path, { form, query }),
  /** Download a file the browser can save (documents, CSV template). */
  async download(path: string, fallbackName: string) {
    const client = actAs.get();
    const res = await fetch(path, { credentials: 'same-origin', headers: client ? { 'X-Act-As-Client': client } : {} });
    if (!res.ok) throw new ApiError(res.status, safeJson(await res.text()) as ApiError['body']);
    const blob = await res.blob();
    const disposition = res.headers.get('content-disposition') ?? '';
    const name = /filename\*=UTF-8''([^;]+)/.exec(disposition)?.[1] ?? /filename="([^"]+)"/.exec(disposition)?.[1];
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name ? decodeURIComponent(name) : fallbackName;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  },
};

export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return 'Something went wrong';
}
